import { ArrowDown, ArrowUp, Minus } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import {
  emvDeltaDirection,
  formatEmvCompact,
  formatEmvDeltaUsd,
} from "@/lib/format-emv"
import { cn } from "@/lib/utils"
import type { EmvKpis } from "@/types/emv-analytics"

/**
 * OKS-503 — KPI strip for the brand EMV dashboard. Four tiles:
 *
 *   1. Total EMV   — cumulative over the range (compact USD).
 *   2. Day over day — latest day's incremental gain. Direction carried
 *      by ▲/▼ glyph + sign (NEVER color alone — UX Spec §6 a11y); "—"
 *      with "first day" caption on the one-snapshot-day edge.
 *   3. Tracked posts — count of posts with EMV in range.
 *   4. Avg EMV / post — total ÷ tracked posts (compact USD).
 *
 * All money arrives as integer cents; formatting is `cents/100` USD.
 * Responsive: 2×2 on mobile, 4-up from `lg`.
 */
interface EmvKpiStripProps {
  kpis: EmvKpis
}

export function EmvKpiStrip({ kpis }: EmvKpiStripProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language

  return (
    <section
      className="grid grid-cols-2 gap-4 lg:grid-cols-4"
      data-testid="emv-kpi-strip"
      aria-label={t("emv.kpis.section_aria")}
    >
      <KpiTile
        testId="emv-kpi-total"
        label={t("emv.kpis.total.label")}
        value={formatEmvCompact(kpis.total_emv_cents, locale)}
        ariaLabel={t("emv.kpis.total.aria", {
          value: formatEmvCompact(kpis.total_emv_cents, locale),
        })}
      />

      <DeltaTile kpis={kpis} locale={locale} />

      <KpiTile
        testId="emv-kpi-tracked-posts"
        label={t("emv.kpis.tracked_posts.label")}
        value={new Intl.NumberFormat(locale).format(kpis.tracked_posts)}
        ariaLabel={t("emv.kpis.tracked_posts.aria", {
          value: kpis.tracked_posts,
        })}
      />

      <KpiTile
        testId="emv-kpi-avg"
        label={t("emv.kpis.avg.label")}
        value={formatEmvCompact(kpis.avg_emv_cents, locale)}
        ariaLabel={t("emv.kpis.avg.aria", {
          value: formatEmvCompact(kpis.avg_emv_cents, locale),
        })}
      />
    </section>
  )
}

interface KpiTileProps {
  testId: string
  label: string
  value: string
  ariaLabel: string
}

function KpiTile({ testId, label, value, ariaLabel }: KpiTileProps) {
  return (
    <Card
      data-testid={testId}
      role="group"
      aria-label={ariaLabel}
      className="rounded-xl border p-5"
    >
      <CardContent className="flex flex-col gap-1 p-0">
        <span className="text-muted-foreground text-sm font-medium">
          {label}
        </span>
        <span
          className="text-foreground text-3xl font-semibold tracking-tight"
          aria-hidden
        >
          {value}
        </span>
      </CardContent>
    </Card>
  )
}

function DeltaTile({ kpis, locale }: { kpis: EmvKpis; locale: string }) {
  const { t } = useTranslation()

  const firstDay = kpis.dod_first_day || kpis.dod_cents === null
  const direction = emvDeltaDirection(kpis.dod_cents)
  const display = formatEmvDeltaUsd(kpis.dod_cents, locale)

  const ariaLabel = firstDay
    ? t("emv.kpis.dod.aria_first_day")
    : direction === "up"
      ? t("emv.kpis.dod.aria_up", { value: display })
      : direction === "down"
        ? t("emv.kpis.dod.aria_down", { value: display })
        : t("emv.kpis.dod.aria_flat")

  const Icon = firstDay
    ? Minus
    : direction === "up"
      ? ArrowUp
      : direction === "down"
        ? ArrowDown
        : Minus

  // Color is a SECONDARY cue — the arrow glyph + sign carry direction.
  const tone =
    firstDay || direction === "flat"
      ? "text-muted-foreground"
      : direction === "up"
        ? "text-emerald-600 dark:text-emerald-400"
        : "text-destructive"

  return (
    <Card
      data-testid="emv-kpi-dod"
      role="group"
      aria-label={ariaLabel}
      className="rounded-xl border p-5"
    >
      <CardContent className="flex flex-col gap-1 p-0">
        <span className="text-muted-foreground text-sm font-medium">
          {t("emv.kpis.dod.label")}
        </span>
        <span
          className={cn(
            "flex items-center gap-1 text-3xl font-semibold tracking-tight",
            tone,
          )}
          aria-hidden
        >
          <Icon className="h-5 w-5 shrink-0" />
          {display}
        </span>
        {firstDay ? (
          <span className="text-muted-foreground text-xs" aria-hidden>
            {t("emv.kpis.dod.first_day")}
          </span>
        ) : null}
      </CardContent>
    </Card>
  )
}
